// Slack Integration Service for Way2Will
// Handles OAuth flow and message fetching from Slack

// OAuth Configuration - Set these in your .env file
const SLACK_CLIENT_ID = import.meta.env.VITE_SLACK_CLIENT_ID || '';
const SLACK_CLIENT_SECRET = import.meta.env.VITE_SLACK_CLIENT_SECRET || '';
const SLACK_REDIRECT_URI = import.meta.env.VITE_SLACK_REDIRECT_URI || 'http://localhost:5173/app/connections/slack/callback';

// Slack OAuth Scopes
export const SLACK_SCOPES = [
    'channels:history',
    'channels:read',
    'groups:history',
    'groups:read',
    'im:history',
    'im:read',
    'mpim:history',
    'mpim:read',
    'users:read',
    'users:read.email',
    'team:read',
];

// Token structure
export interface SlackTokens {
    access_token: string;
    refresh_token?: string;
    expires_at?: number;
    scope: string;
    team_id: string;
    team_name: string;
}

// Slack user info
export interface SlackUserInfo {
    user_id: string;
    team_id: string;
    team_name: string;
    user_name?: string;
    user_email?: string;
    user_image?: string;
}

// Slack message structure
export interface SlackMessage {
    ts: string;
    channel: string;
    channel_name?: string;
    user: string;
    user_name?: string;
    user_image?: string;
    text: string;
    type: 'message' | 'mention' | 'dm';
    timestamp: Date;
}

// Storage keys
const STORAGE_KEY_TOKENS = 'way2will_slack_tokens';
const STORAGE_KEY_USER = 'way2will_slack_user';

/**
 * Check if Slack OAuth is properly configured
 */
export function isSlackConfigured(): boolean {
    return !!SLACK_CLIENT_ID && !!SLACK_CLIENT_SECRET;
}

/**
 * Initiate Slack OAuth flow
 */
export function initiateSlackOAuth(): void {
    if (!SLACK_CLIENT_ID) {
        console.error('Slack Client ID not configured. Set VITE_SLACK_CLIENT_ID in .env');
        alert('Slack integration not configured. Please set up credentials in .env file.');
        return;
    }

    // Generate state for CSRF protection
    const state = JSON.stringify({
        service: 'slack',
        timestamp: Date.now(),
        nonce: Math.random().toString(36).substring(7),
    });

    // Store state for verification on callback
    localStorage.setItem('slack_oauth_state', state);

    // Build OAuth URL
    const params = new URLSearchParams({
        client_id: SLACK_CLIENT_ID,
        redirect_uri: SLACK_REDIRECT_URI,
        scope: SLACK_SCOPES.join(','),
        state: state,
    });

    const authUrl = `https://slack.com/oauth/v2/authorize?${params.toString()}`;

    // Redirect to Slack OAuth
    window.location.href = authUrl;
}

/**
 * Exchange authorization code for tokens
 */
export async function exchangeSlackCodeForTokens(code: string): Promise<SlackTokens | null> {
    if (!SLACK_CLIENT_ID || !SLACK_CLIENT_SECRET) {
        console.error('Slack credentials not configured');
        return null;
    }

    try {
        const response = await fetch('https://slack.com/api/oauth.v2.access', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                code,
                client_id: SLACK_CLIENT_ID,
                client_secret: SLACK_CLIENT_SECRET,
                redirect_uri: SLACK_REDIRECT_URI,
            }),
        });

        const data = await response.json();

        if (!data.ok) {
            console.error('Slack token exchange failed:', data.error);
            return null;
        }

        const tokens: SlackTokens = {
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            scope: data.scope,
            team_id: data.team?.id || '',
            team_name: data.team?.name || '',
        };

        // Store tokens
        localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(tokens));

        // Fetch and store user info
        await fetchAndStoreUserInfo(tokens.access_token);

        return tokens;
    } catch (error) {
        console.error('Error exchanging Slack code for tokens:', error);
        return null;
    }
}

/**
 * Fetch and store user information
 */
async function fetchAndStoreUserInfo(accessToken: string): Promise<SlackUserInfo | null> {
    try {
        const response = await fetch('https://slack.com/api/auth.test', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });

        const data = await response.json();

        if (!data.ok) {
            console.error('Failed to fetch Slack user info:', data.error);
            return null;
        }

        const userInfo: SlackUserInfo = {
            user_id: data.user_id,
            team_id: data.team_id,
            team_name: data.team,
            user_name: data.user,
        };

        localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(userInfo));

        return userInfo;
    } catch (error) {
        console.error('Error fetching Slack user info:', error);
        return null;
    }
}

/**
 * Get stored tokens
 */
export function getSlackTokens(): SlackTokens | null {
    const stored = localStorage.getItem(STORAGE_KEY_TOKENS);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Get stored user info
 */
export function getSlackUserInfo(): SlackUserInfo | null {
    const stored = localStorage.getItem(STORAGE_KEY_USER);
    if (!stored) return null;

    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

/**
 * Check if Slack is connected
 */
export function isSlackConnected(): boolean {
    return !!getSlackTokens();
}

/**
 * Revoke Slack access and clear stored data
 */
export async function revokeSlackAccess(): Promise<void> {
    const tokens = getSlackTokens();

    if (tokens?.access_token) {
        try {
            await fetch('https://slack.com/api/auth.revoke', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${tokens.access_token}`,
                },
            });
        } catch (error) {
            console.error('Error revoking Slack token:', error);
        }
    }

    // Clear local storage
    localStorage.removeItem(STORAGE_KEY_TOKENS);
    localStorage.removeItem(STORAGE_KEY_USER);
}

/**
 * Fetch recent messages and mentions from Slack
 */
export async function fetchSlackMessages(): Promise<SlackMessage[]> {
    const tokens = getSlackTokens();
    if (!tokens?.access_token) {
        console.error('No Slack tokens available');
        return [];
    }

    const messages: SlackMessage[] = [];

    try {
        // Fetch DMs and mentions from conversations
        const conversationsResponse = await fetch(
            'https://slack.com/api/conversations.list?types=im,mpim',
            {
                headers: {
                    Authorization: `Bearer ${tokens.access_token}`,
                },
            }
        );

        const conversationsData = await conversationsResponse.json();

        if (!conversationsData.ok) {
            console.error('Failed to fetch Slack conversations:', conversationsData.error);
            return [];
        }

        // Get messages from each conversation (limit to 5 most recent per conversation)
        for (const channel of conversationsData.channels?.slice(0, 10) || []) {
            const historyResponse = await fetch(
                `https://slack.com/api/conversations.history?channel=${channel.id}&limit=5`,
                {
                    headers: {
                        Authorization: `Bearer ${tokens.access_token}`,
                    },
                }
            );

            const historyData = await historyResponse.json();

            if (historyData.ok && historyData.messages) {
                for (const msg of historyData.messages) {
                    if (msg.type === 'message' && msg.text) {
                        messages.push({
                            ts: msg.ts,
                            channel: channel.id,
                            channel_name: channel.name || 'Direct Message',
                            user: msg.user || 'unknown',
                            text: msg.text,
                            type: channel.is_im ? 'dm' : 'message',
                            timestamp: new Date(parseFloat(msg.ts) * 1000),
                        });
                    }
                }
            }
        }

        // Sort by timestamp, newest first
        messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

        return messages.slice(0, 20); // Return max 20 messages
    } catch (error) {
        console.error('Error fetching Slack messages:', error);
        return [];
    }
}

/**
 * Convert Slack messages to inbox items format
 */
export function slackMessagesToInboxItems(messages: SlackMessage[]): Array<{
    id: string;
    title: string;
    preview: string;
    source: 'slack';
    sourceIcon: string;
    timestamp: Date;
    category: 'actionable' | 'fyi' | 'bundled';
    isRead: boolean;
    slackData: SlackMessage;
}> {
    return messages.map((msg) => ({
        id: `slack-${msg.ts}-${msg.channel}`,
        title: msg.type === 'dm' ? `DM from ${msg.user_name || 'Someone'}` : `#${msg.channel_name || 'channel'}`,
        preview: msg.text.substring(0, 100) + (msg.text.length > 100 ? '...' : ''),
        source: 'slack' as const,
        sourceIcon: '💬',
        timestamp: msg.timestamp,
        category: msg.type === 'dm' || msg.type === 'mention' ? 'actionable' : 'fyi',
        isRead: false,
        slackData: msg,
    }));
}
